import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export interface ConsumptionBasedGHGData {
  year: number;
  transportation: number;
  housing: number;
  food: number;
  goods: number;
  services: number;
}

export interface ConsumptionBasedGHGResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      fill?: boolean;
      tension?: number;
    }>;
  };
  chartOptions: any;
  totalEmissions: number;
  latestYear: number;
  percentChange: number;
  transportationPercent: number;
  housingPercent: number;
  foodPercent: number;
  goodsPercent: number;
  servicesPercent: number;
  // New smart metrics
  californiaAverage: number;
  locationVsCalifornia: number; // percentage
  transportationShare: number; // percentage
}

export async function fetchConsumptionBasedGHGData(location: string): Promise<ConsumptionBasedGHGResult> {
  try {
    console.log('🔍 Fetching consumption-based GHG data for:', location);
    
    // Initialize location service
    await supabaseLocationService.initialize();
    
    // Get geofips for the location
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      throw new Error(`Could not find geofips for location: ${location}`);
    }
    
    console.log('🔍 Geofips for location:', geofips);
    
    // Get location info to determine if it's a city or county
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('location_name, geography_type, county')
      .eq('geofips', geofips)
      .limit(1);
    
    if (locationError) {
      console.error('Error fetching location data:', locationError);
      throw locationError;
    }
    
    if (!locationData || locationData.length === 0) {
      throw new Error(`No location data found for geofips: ${geofips}`);
    }
    
    const locationInfo = locationData[0];
    console.log('🔍 Location info:', locationInfo);
    
    // Determine which table to use based on geography type
    let tableName: string;
    let queryGeofips = geofips;
    
    if (locationInfo.geography_type === 'City') {
      tableName = 'cbei_cities';
      console.log('🔍 Using cbei_cities table for city');
    } else if (locationInfo.geography_type === 'County') {
      tableName = 'cbei_counties';
      console.log('🔍 Using cbei_counties table for county');
    } else {
      throw new Error(`Unsupported geography type: ${locationInfo.geography_type}`);
    }
    
    // Query the appropriate table for all available years
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('year, transportation_total_emissions, housing_total_emissions, food_total_emissions, goods_total_emissions, services_total_emissions, hhcount')
      .eq('geofips', queryGeofips)
      .order('year', { ascending: true });
    
    if (error) {
      console.error(`Error fetching consumption-based GHG data from ${tableName}:`, error);
      throw error;
    }
    
    console.log('🔍 Raw data from', tableName, ':', data?.length || 0, 'records');
    
    if (!data || data.length === 0) {
      throw new Error(`No consumption-based GHG data found for ${location} in ${tableName}`);
    }
    
    // Process all available years of real data
    const historicalData: ConsumptionBasedGHGData[] = data.map(row => {
      const hhcount = row.hhcount || 1; // Fallback to 1 if hhcount is missing
      
      return {
        year: row.year,
        transportation: Math.round(((row.transportation_total_emissions || 0) / 1000000) * hhcount),
        housing: Math.round(((row.housing_total_emissions || 0) / 1000000) * hhcount),
        food: Math.round(((row.food_total_emissions || 0) / 1000000) * hhcount),
        goods: Math.round(((row.goods_total_emissions || 0) / 1000000) * hhcount),
        services: Math.round(((row.services_total_emissions || 0) / 1000000) * hhcount)
      };
    });
    
    console.log('🔍 Historical data processed:', historicalData.length, 'years of real data');
    
    // Prepare chart data
    const years = historicalData.map(d => d.year);
    const categories = ['Transportation', 'Housing', 'Food', 'Goods', 'Services'];
    const colors = ['#1e3a8a', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    
    const datasets = categories.map((category, index) => {
      const fieldMap: { [key: string]: keyof ConsumptionBasedGHGData } = {
        'Transportation': 'transportation',
        'Housing': 'housing',
        'Food': 'food',
        'Goods': 'goods',
        'Services': 'services'
      };
      
      const field = fieldMap[category];
      const data = historicalData.map(d => d[field]);
      
      return {
        label: category,
        data,
        backgroundColor: colors[index],
        borderColor: colors[index],
        fill: false,
        tension: 0.2
      };
    });
    
    // Calculate metrics
    const latestData = historicalData[historicalData.length - 1];
    const earliestData = historicalData[0];
    const totalEmissions = latestData.transportation + latestData.housing + latestData.food + latestData.goods + latestData.services;
    const percentChange = earliestData ? 
      ((totalEmissions - (earliestData.transportation + earliestData.housing + earliestData.food + earliestData.goods + earliestData.services)) / 
       (earliestData.transportation + earliestData.housing + earliestData.food + earliestData.goods + earliestData.services)) * 100 : 0;
    
    const transportationPercent = totalEmissions > 0 ? (latestData.transportation / totalEmissions) * 100 : 0;
    const housingPercent = totalEmissions > 0 ? (latestData.housing / totalEmissions) * 100 : 0;
    const foodPercent = totalEmissions > 0 ? (latestData.food / totalEmissions) * 100 : 0;
    const goodsPercent = totalEmissions > 0 ? (latestData.goods / totalEmissions) * 100 : 0;
    const servicesPercent = totalEmissions > 0 ? (latestData.services / totalEmissions) * 100 : 0;
    
    // Calculate California average for comparison
    let californiaAverage = 0;
    let locationVsCalifornia = 0;
    
    try {
      // Get California state data for comparison
      const { data: californiaData, error: californiaError } = await supabaseClient
        .from('cbei_counties')
        .select('transportation_total_emissions, housing_total_emissions, food_total_emissions, goods_total_emissions, services_total_emissions, hhcount')
        .eq('geofips', 6000) // California state geofips
        .eq('year', latestData.year) // Use the same year as the location data
        .limit(1);
      
      if (!californiaError && californiaData && californiaData.length > 0) {
        const caRow = californiaData[0];
        const caHhcount = caRow.hhcount || 1; // Fallback to 1 if hhcount is missing
        const caTransportation = ((caRow.transportation_total_emissions || 0) / 1000000) * caHhcount;
        const caHousing = ((caRow.housing_total_emissions || 0) / 1000000) * caHhcount;
        const caFood = ((caRow.food_total_emissions || 0) / 1000000) * caHhcount;
        const caGoods = ((caRow.goods_total_emissions || 0) / 1000000) * caHhcount;
        const caServices = ((caRow.services_total_emissions || 0) / 1000000) * caHhcount;
        
        californiaAverage = caTransportation + caHousing + caFood + caGoods + caServices;
        locationVsCalifornia = californiaAverage > 0 ? (totalEmissions / californiaAverage) * 100 : 0;
        
        console.log('🔍 California comparison:', {
          californiaAverage,
          totalEmissions,
          locationVsCalifornia
        });
      } else {
        // Fallback: use a reasonable California average if data not available
        // Since we're now showing community totals, we'll use a typical California community size
        californiaAverage = 15.2 * 100000; // MT CO₂e for a typical CA community (~100k households)
        locationVsCalifornia = (totalEmissions / californiaAverage) * 100;
        console.log('🔍 Using fallback California average:', californiaAverage);
      }
    } catch (error) {
      console.warn('Error fetching California average data:', error);
      // Fallback values
      californiaAverage = 15.2 * 100000; // MT CO₂e for a typical CA community (~100k households)
      locationVsCalifornia = (totalEmissions / californiaAverage) * 100;
    }
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: `Consumption-Based GHG Inventory - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        tooltip: {
          callbacks: {
            label: function(context: any) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              label += new Intl.NumberFormat('en-US', { style: 'decimal' }).format(context.parsed.y) + ' MT CO₂e';
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Emissions (Metric Tons CO₂e per Community)'
          },
          ticks: {
            callback: (value: any) => Number(value).toLocaleString()
          }
        }
      }
    };
    
    const result: ConsumptionBasedGHGResult = {
      chartData: {
        labels: years,
        datasets
      },
      chartOptions,
      totalEmissions,
      latestYear: latestData.year,
      percentChange,
      transportationPercent,
      housingPercent,
      foodPercent,
      goodsPercent,
      servicesPercent,
      californiaAverage,
      locationVsCalifornia,
      transportationShare: transportationPercent
    };
    
    console.log('✅ Consumption-based GHG data processed successfully:', {
      totalEmissions,
      percentChange,
      transportationPercent,
      housingPercent,
      tableUsed: tableName
    });
    
    return result;
    
  } catch (error) {
    console.error('Error fetching consumption-based GHG data:', error);
    throw error;
  }
}
